import { Clock, CheckCircle, XCircle } from 'lucide-react';
import type { ServiceWithPrice } from '@/types';
import { cn } from '@/utils/cn';

interface ServiceCardProps {
  service: ServiceWithPrice;
  onClick?: (service: ServiceWithPrice) => void;
  selected?: boolean;
  showPrice?: boolean;
  className?: string;
}

/**
 * ServiceCard Component
 * Display service information with pricing and availability
 */
export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  onClick,
  selected = false,
  showPrice = true,
  className = '',
}) => {
  const isClickable = !!onClick;

  // Icon mapping for service types
  const getServiceIcon = (serviceType: string) => {
    const icons: Record<string, string> = {
      tire_change: '🔧',
      battery_subscription: '🔋',
      fuel_delivery: '⛽',
      external_patch: '🛠️',
      internal_patch: '🔨',
      battery_transfer: '🚗',
      other: '📦',
    };
    return icons[serviceType] || '📦';
  };

  return (
    <div
      onClick={isClickable ? () => onClick(service) : undefined}
      className={cn(
        'card p-4 transition-all duration-200',
        isClickable && 'cursor-pointer hover:shadow-glow hover:border-primary/50',
        selected && 'border-primary shadow-glow',
        !service.isAvailable && 'opacity-60',
        className
      )}
    >
      {/* Service Header */}
      <div className="flex items-start gap-3 mb-3">
        {/* Icon */}
        <div className="text-3xl flex-shrink-0">
          {service.icon ? (
            <span>{getServiceIcon(service.serviceType)}</span>
          ) : (
            getServiceIcon(service.serviceType)
          )}
        </div>

        {/* Service Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-100 text-lg mb-1 truncate">
            {service.nameEn}
          </h3>
          <p className="text-sm text-gray-400 mb-2">{service.nameAr}</p>

          {service.descriptionEn && (
            <p className="text-xs text-gray-500 line-clamp-2">
              {service.descriptionEn}
            </p>
          )}
        </div>

        {/* Availability Badge */}
        <div className="flex-shrink-0">
          {service.isAvailable ? (
            <div className="flex items-center gap-1 text-green-500 text-xs">
              <CheckCircle className="h-4 w-4" />
              <span>متاح</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-red-500 text-xs">
              <XCircle className="h-4 w-4" />
              <span>غير متاح</span>
            </div>
          )}
        </div>
      </div>

      {/* Service Details */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-700">
        {/* Price */}
        {showPrice && (
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-primary">
              {service.price.toFixed(3)}
            </span>
            <span className="text-sm text-gray-400">KD</span>
          </div>
        )}

        {/* Estimated Time */}
        {service.estimatedTimeMinutes && (
          <div className="flex items-center gap-1 text-gray-400 text-sm">
            <Clock className="h-4 w-4" />
            <span>~{service.estimatedTimeMinutes} دقيقة</span>
          </div>
        )}
      </div>

      {/* Service Type Badge */}
      <div className="mt-3">
        <span className="inline-block px-2 py-1 bg-dark text-xs text-gray-400 rounded">
          {service.serviceType.replace('_', ' ').toUpperCase()}
        </span>
      </div>

      {/* Selected Indicator */}
      {selected && (
        <div className="absolute top-2 right-2 text-primary">
          <CheckCircle className="h-5 w-5 fill-current" />
        </div>
      )}
    </div>
  );
};
